import {naturalSort, naturalSortByFunction, naturalSortByKey, objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchemaSummary} from 'constants/RegistryConstants';
import * as SampleConstants from 'constants/SampleConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList, {getChipsRowHeight} from 'ui-library/chips/ChipList';
import {ScrollableTableColumn, TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';
import {capwords, typeToName} from 'util/NameUtil';

interface DataById {
  [storableId: string]: {
    [accessorKey: string]: JSX.Element | number;
  };
}

interface RenderedStorableSummaryExtras {
  columns: Array<ScrollableTableColumn>;
  dataById: DataById;
}

interface ChildCountsByTypeName {
  [typeName: string]: {
    [storableId: string]: number;
  };
}

/**
 * Get child storable counts grouped by type name for each input location. The type name is storable schema
 * name for locations ("Building", "Fridge", etc.) and storable type ("Box", "Container", "Plate") for
 * non-locations.
 * @param  {Object} childStorableSchemasByLocationId - map of the form
 *                    locationId -> [{childStorableSchema, numChildStorables}]
 * @param  {Array}  storableIds - array of storable IDs
 * @return {Object} map of the form
 *                    typeName -> storableId -> childCount
 */
function getChildCountsByTypeName(
  storableIds: Array<string>,
  childStorableSchemasByLocationId?: SampleConstants.ChildStorableSchemasByLocationId
): ChildCountsByTypeName {
  const childCountsByTypeName: ChildCountsByTypeName = {};
  for (const storableId of storableIds) {
    const childStorableSchemas = childStorableSchemasByLocationId?.[storableId] ?? [];
    for (const {childStorableSchema, numChildStorables} of childStorableSchemas) {
      const storableSchemaType = APIUtil.apiIdToType(childStorableSchema.id);
      const storableType = SampleConstants.STORABLE_SCHEMA_TYPE_TO_STORABLE_TYPE[storableSchemaType];
      const typeName =
        storableType === ModelTypeConstants.LOCATION
          ? childStorableSchema.name
          : capwords(typeToName(storableType));
      childCountsByTypeName[typeName] = childCountsByTypeName[typeName] || {};
      childCountsByTypeName[typeName][storableId] =
        (childCountsByTypeName[typeName][storableId] || 0) + numChildStorables;
    }
  }
  return childCountsByTypeName;
}

interface ChildContainerStatusesProps {
  childContainerStatuses: {
    numFilledContainers: number;
    numTotalContainers: number;
  };
}

/**
 * Display grid's child container statuses:
 *   | Containers          |
 *   | 29 Total, 28 Filled |
 */
function ChildContainerStatuses(props: ChildContainerStatusesProps): JSX.Element {
  const {numFilledContainers, numTotalContainers} = props.childContainerStatuses;
  return (
    <span>
      {numTotalContainers} Total, {numFilledContainers} Filled
    </span>
  );
}

ChildContainerStatuses.KEY = 'childContainerStatusesByGridId';
ChildContainerStatuses.COLUMN = {header: 'Containers', accessorKey: 'childContainerStatuses'};
ChildContainerStatuses.isPresent = (childContainerStatuses: {numTotalContainers: number}) =>
  childContainerStatuses && childContainerStatuses.numTotalContainers;

interface ParentFileSchemasProps {
  parentFileSchemas: Array<TagSchemaSummary>;
}

/**
 * Display grid's child containers' parent file schemas:
 *   | Contents |
 *   | Vector   |
 */
function ParentFileSchemas(props: ParentFileSchemasProps): JSX.Element {
  const sortedFileSchemaNames = naturalSort(props.parentFileSchemas.map(({name}) => name));
  return (
    <Tooltip title={sortedFileSchemaNames.join('\n')} delay={350}>
      <span>{sortedFileSchemaNames.join(', ')}</span>
    </Tooltip>
  );
}

ParentFileSchemas.KEY = 'childContainerParentFileSchemasByGridId';
ParentFileSchemas.COLUMN = {header: 'Content types', accessorKey: 'parentFileSchemas'};
ParentFileSchemas.isPresent = (parentFileSchemas: Array<TagSchemaSummary>) =>
  parentFileSchemas && parentFileSchemas.length;

interface ContainedItemsProps {
  containedItems: Array<SampleConstants.ContainableSummary>;
}

/**
 * Display container's contained items:
 *   | Contents                           |
 *   | pBN000-20170328, pBN000-20170328-2 |
 */
function ContainedItems(props: ContainedItemsProps): JSX.Element {
  const sortedContainedItems = naturalSortByKey(props.containedItems, 'name');
  return <ChipList itemIds={sortedContainedItems.map(({id}) => id)} chipSize={ChipSize.SMALL} />;
}

ContainedItems.KEY = 'containedItemsByContainerId';
ContainedItems.COLUMN = {header: 'Contents', accessorKey: 'containedItems'};
ContainedItems.isPresent = (containedItems: Array<SampleConstants.ContainableSummary>) =>
  containedItems && containedItems.length;

/**
 * Render extra storable summary data for storable listing. Return columns and cells for caller to render.
 * @param  {Object} storableSummaryExtras - map of the form
                      {childStorableSchemasByLocationId, childContainerStatusesByGridId,
                       childContainerParentFileSchemasByGridId, containedItemsByContainerId}
 * @param  {Array}  storableIds - array of storable IDs
 * @return {Object} map of the form
 *                    {columns, dataById}
 *                    where `columns` is an array of the form
 *                      [{header, accessorKey}]
 *                    and `dataById` is a map of the form
 *                      storableId -> accessorKey -> renderedCell
 */
export function render(
  storableSummaryExtras: SampleConstants.StorableSummaryExtras,
  storableIds: Array<string>
): RenderedStorableSummaryExtras {
  const retval: RenderedStorableSummaryExtras = {
    columns: [],
    dataById: objectFromEntries(storableIds.map((storableId) => [storableId, {}])),
  };
  if (Object.keys(storableSummaryExtras).length === 0) {
    return retval;
  }

  // Display location's child counts, separate columns for each type:
  //   | Box Count | Fridge Count | Plate Count |
  //   | 4         | 1            | 2           |
  const childCountsByTypeName = getChildCountsByTypeName(
    storableIds,
    storableSummaryExtras.childStorableSchemasByLocationId
  );
  for (const [typeName, childCounts] of naturalSortByFunction(
    Object.entries(childCountsByTypeName),
    ([typeName, childCounts]) => typeName
  )) {
    const accessorKey = `childStorableSchemas:${typeName}`;
    // 1. Add column to `columns`
    retval.columns.push({header: `${typeName} count`, accessorKey});
    // 2. Add rendered cells to row `dataById`, keyed by (`storableId`, `accessorKey`)
    for (const [storableId, childCount] of Object.entries(childCounts)) {
      assert(childCount > 0, 'Expected childCount > 0');
      retval.dataById[storableId][accessorKey] = childCount;
    }
  }

  /**
   * TODO: refactor this loop to eliminate the remaining two // @ts-expect-error comments.
   * The issue is that the loop is iterating over components, each of which has their own properties
   * interface. Some possibilities for refactor include:
   *    1.) Replacing the loop with a declarative code block so that each component can have its own variable.
   *    2.) Use type-narrowing guards to determine at run-time which StorableSummaryExtra property is being
   *        accessed, and therefore which component to render.
   */
  for (const component of [ChildContainerStatuses, ParentFileSchemas, ContainedItems]) {
    // @ts-expect-error
    const valuesById = storableSummaryExtras[component.KEY];
    // If no values are present for this column, skip it
    const shouldAddColumn =
      valuesById && storableIds.some((storableId: string) => component.isPresent(valuesById[storableId]));
    if (!shouldAddColumn) {
      continue;
    }
    // 1. Add column to `columns`
    retval.columns.push(component.COLUMN);
    // 2. Add rendered cells to row `dataById`, keyed by (`storableId`, `accessorKey`)
    for (const storableId of storableIds) {
      const value = valuesById[storableId];
      // If no value is present for this cell, skip it
      if (!component.isPresent(value)) {
        continue;
      }
      // @ts-expect-error
      const renderedCell = React.createElement(component, {[component.COLUMN.accessorKey]: value});
      retval.dataById[storableId][component.COLUMN.accessorKey] = renderedCell;
    }
  }

  return retval;
}

export function getRowContentHeight(
  storableSummaryExtras: SampleConstants.StorableSummaryExtras,
  storableId: string
): number {
  if (Object.keys(storableSummaryExtras).length === 0) {
    return TABLE_ROW_BODY_HEIGHT;
  }
  const containedItems = storableSummaryExtras.containedItemsByContainerId?.[storableId] ?? null;
  if (!containedItems || !containedItems.length) {
    return TABLE_ROW_BODY_HEIGHT;
  }
  return getChipsRowHeight(containedItems.length, ChipSize.SMALL);
}
